import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Music, 
  Search, 
  Plus, 
  Trash2, 
  Play, 
  SkipForward, 
  Settings2, 
  Activity,
  Zap,
  Layers,
  Heart,
  Share2,
  ListMusic,
  Waves,
  RefreshCcw,
  Volume2,
  Gauge,
  Shield,
  Maximize2,
  Scissors,
  Palette
} from 'lucide-react';
import { SimulationConfig, EscapeColorMode } from '@/types';
import { BALL_SOUND_EFFECTS } from '@/constants/constants';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';
import { AUDIO, MELODIES } from '@/services/AudioService';
import { CollapsibleSection } from './CollapsibleSection';

interface RhythmicModeSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  onReset: () => void;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
  getLockInfo?: (settingId: string) => any;
}

export const RhythmicModeSection: React.FC<RhythmicModeSectionProps> = ({ 
    config, setConfig, onReset, isLocked, minPlan = "FREE", onUpgradeClick, getLockInfo 
}) => {
  const [selectedMelodyId, setSelectedMelodyId] = useState<string>(config.selectedMelody || '');
  const ringPatterns: EscapeColorMode[] = ['RAINBOW', 'ALTERNATING', 'GRADIENT', 'UNIFORM', 'INDIVIDUAL'];

  const handleIndividualColorChange = (index: number, color: string) => {
    setConfig(p => {
        const current = Array.isArray(p.escapeIndividualColors) ? p.escapeIndividualColors : [];
        const newColors = [...current];
        newColors[index] = color;
        return { ...p, escapeIndividualColors: newColors };
    });
  };

  const ColorInput = ({ label, value, onChange, tooltip, settingKey, disabled, disabledMessage, modes }: { label: string, value: string, onChange: (v: string) => void, tooltip?: string, settingKey?: string, disabled?: boolean, disabledMessage?: string, modes?: string[] }) => (
    <ControlGroup label={label} tooltip={tooltip} settingKey={settingKey} disabled={disabled} disabledMessage={disabledMessage} modes={modes} darkMode={config.darkMode}>
        <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
            <div className="relative group">
                <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
                <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
            </div>
            <input 
                type="text" 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                className={`flex-1 px-3 py-2 text-[11px] font-mono font-bold border rounded-xl outline-none transition-all ${
                    config.darkMode 
                    ? 'bg-black/20 border-white/5 text-white focus:border-white/20' 
                    : 'bg-neutral-50 border-neutral-100 text-neutral-800 focus:border-neutral-300'
                }`} 
            />
        </div>
    </ControlGroup>
  );

  const syncMelody = () => {
    if (!selectedMelodyId) return;
    setConfig(prev => ({ ...prev, selectedMelody: selectedMelodyId }));
    AUDIO.resetIndex();
    onReset();
  };

  return (
    <CollapsibleSection 
        title="Rhythmic Mode Settings" 
        icon={Music} 
        darkMode={config.darkMode}
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        settingKey="rhythmicStudio"
        onUpgradeClick={onUpgradeClick}
        defaultOpen
    >
        <div className="flex flex-col gap-6 mt-4">
            <div className="space-y-10">
                {/* 1. Track Selection */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ListMusic className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Active Song / Melody</h4>
                    </div>
                    
                    <div className="flex flex-col gap-3">
                        <select 
                            value={selectedMelodyId}
                            onChange={(e) => setSelectedMelodyId(e.target.value)}
                            className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                        >
                            <option value="">No Melody Selected</option>
                            {Object.keys(MELODIES).map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                            {config.customMelodies?.length > 0 && <optgroup label="Custom Melodies">
                                {config.customMelodies.map(m => (
                                    <option key={m.id} value={m.name}>{m.name} ({m.format})</option>
                                ))}
                            </optgroup>}
                        </select>

                        <button 
                            onClick={syncMelody} 
                            disabled={!selectedMelodyId}
                            className={`w-full py-4 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${!selectedMelodyId ? 'bg-zinc-500/20 opacity-50 cursor-not-allowed text-zinc-400' : 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/20 active:scale-95'}`}
                        >
                            <RefreshCcw className={`w-3.5 h-3.5 ${!selectedMelodyId ? '' : 'animate-spin-slow'}`} />
                            Reset & Sync Melody
                        </button>
                    </div>
                </div>

                {/* 2. Interaction Mode */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Settings2 className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Ball Movement Mode</h4>
                        <InfoPopup settingKey="rhythmicSyncMode" darkMode={config.darkMode} />
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                        {[
                            { id: 'RADIUS_AND_SPEED', label: 'Radius + Speed (Dynamic)', desc: 'Adjusts both outer size and ball speed.' },
                            { id: 'SPEED_ONLY', label: 'Speed Only (Fixed Radius)', desc: 'Keeps outer size fixed, adjusts ball speed.' },
                            { id: 'DIRECTION_ONLY', label: 'Direction Only (Fixed Speed)', desc: 'Keeps speed fixed, adjusts bounce direction.' },
                            { id: 'CURVED_PATH', label: 'Curved Path (Fixed Radius)', desc: 'Bounces in arcs around the circle.' }
                        ].map(mode => (
                            <button
                                key={mode.id}
                                onClick={() => setConfig(p => ({ ...p, rhythmicSyncMode: mode.id as any }))}
                                className={`text-left p-4 rounded-xl border transition-all duration-300 ${config.rhythmicSyncMode === mode.id ? (config.darkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-zinc-950 shadow-sm text-zinc-950') : 'bg-transparent border-transparent opacity-40 hover:opacity-60'}`}
                            >
                                <div className="text-[10px] font-black uppercase tracking-tight flex items-center justify-between">
                                    {mode.label}
                                    {config.rhythmicSyncMode === mode.id && <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.5)]" />}
                                </div>
                                <div className="text-[8px] font-bold opacity-40 uppercase tracking-widest mt-1">{mode.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* 3. Animation Sync */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Animation Sync</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <ModifierCard 
                            label="Pulse Animation" 
                            settingKey="rhythmicPulseActive"
                            active={config.rhythmicPulseActive} 
                            onToggle={() => setConfig(p => ({...p, rhythmicPulseActive: !p.rhythmicPulseActive}))} 
                            darkMode={config.darkMode}
                            icon={Activity}
                        />

                        <ModifierCard 
                            label="Glow Animation" 
                            settingKey="rhythmicGlowActive"
                            active={config.rhythmicGlowActive} 
                            onToggle={() => setConfig(p => ({...p, rhythmicGlowActive: !p.rhythmicGlowActive}))} 
                            darkMode={config.darkMode}
                            icon={Zap}
                        />
                    </div>
                    <div className="flex flex-col gap-4 mt-4">
                        <SmartInput label="Animation Sensitivity" settingKey="rhythmicSensitivity" min={0.1} max={5} step={0.1} value={config.rhythmicSensitivity} onChange={v => setConfig(p => ({...p, rhythmicSensitivity: v}))} darkMode={config.darkMode} />
                        <SmartInput label="Ring Pulse Sensitivity" settingKey="rhythmicBreakThreshold" min={0} max={1} step={0.01} value={config.rhythmicBreakThreshold} onChange={v => setConfig(p => ({...p, rhythmicBreakThreshold: v}))} darkMode={config.darkMode} />
                    </div>
                </div>

                {/* 4. Sync Controls (Restored) */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Gauge className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Sync Controls</h4>
                    </div>
                    <div className="flex flex-col gap-5">
                        <SmartInput 
                            label="Base Circle Size" 
                            settingKey="containerRadius"
                            min={50} max={500} 
                            value={config.containerRadius} 
                            onChange={v => setConfig(p => ({...p, containerRadius: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Playback Speed" 
                            settingKey="rhythmicTimeScale"
                            min={0.1} max={4} step={0.1} 
                            value={config.rhythmicTimeScale} 
                            onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Ball Visual Speed" 
                            settingKey="rhythmicBallSpeedScale"
                            min={0.1} max={10} step={0.1} 
                            value={config.rhythmicBallSpeedScale} 
                            onChange={v => setConfig(p => ({...p, rhythmicBallSpeedScale: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Sync Offset (ms)" 
                            settingKey="rhythmicSyncOffset"
                            min={-500} max={500} 
                            value={config.rhythmicSyncOffset} 
                            onChange={v => setConfig(p => ({...p, rhythmicSyncOffset: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <ControlGroup label="Decouple Ball Speed" settingKey="rhythmicBallSpeedDecoupled" darkMode={config.darkMode}>
                                <Toggle active={config.rhythmicBallSpeedDecoupled} onClick={() => setConfig(p => ({...p, rhythmicBallSpeedDecoupled: !p.rhythmicBallSpeedDecoupled}))} />
                            </ControlGroup>
                            <ControlGroup label="Decouple Sync" settingKey="rhythmicDecoupleSpeed" darkMode={config.darkMode}>
                                <Toggle active={config.rhythmicDecoupleSpeed} onClick={() => setConfig(p => ({...p, rhythmicDecoupleSpeed: !p.rhythmicDecoupleSpeed}))} />
                            </ControlGroup>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Maximize2 className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Physics & Path Constraints</h4>
                    </div>
                    <div className="flex flex-col gap-5">
                        <SmartInput 
                            label="Min Bounce Distance" 
                            settingKey="rhythmicMinBounceDistance"
                            min={0} max={400} 
                            value={config.rhythmicMinBounceDistance} 
                            onChange={v => setConfig(p => ({...p, rhythmicMinBounceDistance: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput 
                                label="Min Speed Cap" 
                                settingKey="rhythmicMinSpeed"
                                min={0} max={200} step={1}
                                value={config.rhythmicMinSpeed} 
                                onChange={v => setConfig(p => ({...p, rhythmicMinSpeed: v}))} 
                                darkMode={config.darkMode} 
                            />
                            <SmartInput 
                                label="Max Speed Cap" 
                                settingKey="rhythmicMaxSpeed"
                                min={0} max={1000} step={1}
                                value={config.rhythmicMaxSpeed} 
                                onChange={v => setConfig(p => ({...p, rhythmicMaxSpeed: v}))} 
                                darkMode={config.darkMode} 
                            />
                        </div>
                        <p className="text-[7px] font-bold opacity-30 uppercase tracking-[0.1em] text-center">Controls velocity limits during rhythmic path calculations.</p>
                    </div>
                </div>

                {/* 5. Max Size Sound Effect (Restored) */}
                <div className="space-y-4 p-4 rounded-3xl border border-zinc-500/10 bg-black/5">
                    <div className="flex items-center gap-2 mb-2">
                        <Volume2 className="w-3.5 h-3.5 opacity-40" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Max Size Sound Effect</h4>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-2 h-10 bg-black/10 rounded-xl p-1">
                            <button 
                                onClick={() => setConfig(p => ({...p, rhythmicMaxSizeSoundType: 'SOUND_EFFECT'}))}
                                className={`rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${config.rhythmicMaxSizeSoundType === 'SOUND_EFFECT' ? 'bg-cyan-600 text-white shadow-lg' : 'opacity-40 text-white hover:opacity-60'}`}
                            >
                                Built-In
                            </button>
                            <button 
                                onClick={() => setConfig(p => ({...p, rhythmicMaxSizeSoundType: 'CUSTOM'}))}
                                className={`rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${config.rhythmicMaxSizeSoundType === 'CUSTOM' ? 'bg-cyan-600 text-white shadow-lg' : 'opacity-40 text-white hover:opacity-60'}`}
                            >
                                Custom
                            </button>
                        </div>
                        
                        {config.rhythmicMaxSizeSoundType === 'SOUND_EFFECT' ? (
                            <select 
                                value={config.rhythmicMaxSizeSound} 
                                onChange={(e) => setConfig(p => ({...p, rhythmicMaxSizeSound: e.target.value}))}
                                className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-900'}`}
                            >
                                {BALL_SOUND_EFFECTS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        ) : (
                            <select 
                                value={config.rhythmicMaxSizeCustomSoundId} 
                                onChange={(e) => setConfig(p => ({...p, rhythmicMaxSizeCustomSoundId: e.target.value}))}
                                className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-900'}`}
                            >
                                <option value="">Select Custom Sound</option>
                                {config.userSounds.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        )}
                        <p className="text-[7px] font-bold opacity-30 uppercase tracking-[0.1em] text-center">Plays when ball reaches max size defined in physics.</p>
                    </div>
                </div>

                {/* 6. Ball Break Effect (Restored) */}
                <div className="space-y-4 p-4 rounded-3xl border border-zinc-500/10 bg-black/5">
                    <div className="flex items-center gap-2 mb-2">
                        <Maximize2 className="w-3.5 h-3.5 opacity-40" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Ball Break Effect</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {['NONE', 'SHATTER', 'PARTICLES', 'EXPLODE'].map(effect => (
                            <button 
                                key={effect}
                                onClick={() => setConfig(p => ({...p, rhythmicBreakEffect: effect as any}))}
                                className={`py-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all ${config.rhythmicBreakEffect === effect ? 'bg-cyan-600 border-cyan-600 text-white shadow-lg shadow-cyan-600/20' : 'bg-white/5 border-transparent opacity-40 text-white hover:opacity-60'}`}
                            >
                                {effect}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 space-y-5">
                        <div className="flex flex-col gap-2">
                            <ControlGroup label="Outer Layer Break Animation" settingKey="rhythmicOuterLayerAnimation" darkMode={config.darkMode}>
                            <select 
                                value={config.rhythmicOuterLayerAnimation} 
                                onChange={(e) => setConfig(p => ({...p, rhythmicOuterLayerAnimation: e.target.value}))}
                                className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-900'}`}
                            >
                                <option value="NONE">None</option>
                                <option value="EXPLOSION">Explosion</option>
                                <option value="ENERGY_ORBS">Energy Orbs</option>
                                <option value="SHOCKWAVE">Shockwave</option>
                                <option value="GLITCH">Glitch Burst</option>
                            </select>
                            </ControlGroup>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput label="Piece Count" settingKey="rhythmicBreakPieceCount" min={2} max={100} value={config.rhythmicBreakPieceCount} onChange={v => setConfig(p => ({...p, rhythmicBreakPieceCount: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Piece Gravity" settingKey="rhythmicBreakGravity" min={0} max={2} step={0.1} value={config.rhythmicBreakGravity} onChange={v => setConfig(p => ({...p, rhythmicBreakGravity: v}))} darkMode={config.darkMode} />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput label="Piece Mode Duration" settingKey="rhythmicBreakDuration" min={500} max={10000} step={100} value={config.rhythmicBreakDuration} onChange={v => setConfig(p => ({...p, rhythmicBreakDuration: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Impact Blast Force" settingKey="rhythmicBreakBlastRadius" min={0} max={500} value={config.rhythmicBreakBlastRadius} onChange={v => setConfig(p => ({...p, rhythmicBreakBlastRadius: v}))} darkMode={config.darkMode} />
                        </div>

                        <SmartInput label="Reset Delay (ms)" settingKey="rhythmicBreakResetDelay" min={500} max={10000} step={100} value={config.rhythmicBreakResetDelay} onChange={v => setConfig(p => ({...p, rhythmicBreakResetDelay: v}))} darkMode={config.darkMode} />
                    </div>
                </div>

                {/* Audio Spectrum Display moved to Physics Tab */}

                {/* Escape Interaction (Hybrid) */}
                {(config.enableEscape || config.mode === 'RHYTHMIC_ESCAPE') && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-3.5 h-3.5 text-amber-500 opacity-40" />
                            <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Escape Mode Interaction</h4>
                        </div>
                        <div className="flex flex-col gap-6">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">Inner Shield Ring</span>
                                        <span className="text-[8px] font-bold opacity-40 uppercase tracking-tight mt-1">Prevents ball from escaping early</span>
                                    </div>
                                    <Toggle active={config.rhythmicEscapeShieldRing} onClick={() => setConfig(p => ({...p, rhythmicEscapeShieldRing: !p.rhythmicEscapeShieldRing}))} />
                                </div>

                                <AnimatePresence>
                                    {config.rhythmicEscapeShieldRing && (
                                        <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden space-y-4 pt-2"
                                        >
                                            <div className="h-px bg-zinc-500/10 mb-4" />
                                            <SmartInput 
                                                label="Shield Radius" 
                                                min={20} max={400} 
                                                value={config.rhythmicEscapeShieldRingRadius} 
                                                onChange={(v: number) => setConfig(p => ({...p, rhythmicEscapeShieldRingRadius: v}))} 
                                                darkMode={config.darkMode} 
                                            />
                                            
                                            <div className="flex flex-col gap-2">
                                                <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30 ml-1">Shield Break Condition</span>
                                                <select 
                                                    value={config.rhythmicEscapeShieldRingDestroyMode} 
                                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyMode: e.target.value as any}))}
                                                    className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-900'}`}
                                                >
                                                    <option value="BOUNCES">Hit Count Limit</option>
                                                    <option value="TIME">Time Limit</option>
                                                </select>
                                            </div>

                                            <SmartInput 
                                                label={config.rhythmicEscapeShieldRingDestroyMode === 'BOUNCES' ? 'Required Hits' : 'Seconds'} 
                                                min={1} max={100} 
                                                value={config.rhythmicEscapeShieldRingDestroyValue} 
                                                onChange={(v: number) => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyValue: v}))} 
                                                darkMode={config.darkMode} 
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <div className="h-px bg-zinc-500/10 my-4" />

                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">Auto-Sync to Beat</span>
                                        <span className="text-[8px] font-bold opacity-40 uppercase tracking-tight mt-1">Locks contact points to the rhythm</span>
                                    </div>
                                    <Toggle active={config.rhythmicEscapeAutoSync} onClick={() => setConfig(p => ({...p, rhythmicEscapeAutoSync: !p.rhythmicEscapeAutoSync}))} />
                                </div>

                                <div className="h-px bg-zinc-500/10 my-4" />

                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Palette className="w-3.5 h-3.5 opacity-20" />
                                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Ring Pattern Studio</h4>
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <ControlGroup label="Pattern Mode" settingKey="escapeColorMode" modes={['ESCAPE']} darkMode={config.darkMode}>
                                            <div className={`p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100'}`}>
                                                <select value={config.escapeColorMode} onChange={e => setConfig(p => ({...p, escapeColorMode: e.target.value as EscapeColorMode}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                                                    {ringPatterns.map(m => <option key={m} value={m} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{m}</option>)}
                                                </select>
                                            </div>
                                        </ControlGroup>
                                        
                                        {config.escapeColorMode === 'RAINBOW' && (
                                            <SmartInput 
                                                label="Spectral Density" 
                                                min={0.1} max={50} step={0.1}
                                                value={config.escapeRainbowSpeed || 1.0} 
                                                onChange={v => setConfig(p => ({...p, escapeRainbowSpeed: v}))} 
                                                darkMode={config.darkMode} 
                                                settingKey="escapeRainbowSpeed"
                                                modes={['ESCAPE']}
                                            />
                                        )}
                                        
                                        {(config.escapeColorMode === 'ALTERNATING' || config.escapeColorMode === 'GRADIENT') && (
                                            <div className="grid grid-cols-1 gap-4">
                                                <ColorInput label="Prism Start" value={config.escapeColorA || '#ff0000'} onChange={v => setConfig(p => ({...p, escapeColorA: v}))} settingKey="escapeColorA" modes={['ESCAPE']} />
                                                <ColorInput label="Prism End" value={config.escapeColorB || '#ffffff'} onChange={v => setConfig(p => ({...p, escapeColorB: v}))} settingKey="escapeColorB" modes={['ESCAPE']} />
                                            </div>
                                        )}
                                        
                                        {config.escapeColorMode === 'UNIFORM' && (
                                            <ColorInput label="Shell Color" value={config.escapeUniformColor || '#3b82f6'} onChange={v => setConfig(p => ({...p, escapeUniformColor: v}))} settingKey="escapeUniformColor" modes={['ESCAPE']} />
                                        )}
                                        
                                        {config.escapeColorMode === 'INDIVIDUAL' && (
                                            <ControlGroup label="Layer Profiles" settingKey="escapeIndividualColors" modes={['ESCAPE']} darkMode={config.darkMode}>
                                                <div className="flex flex-col gap-3 w-full">
                                                    <div className="grid grid-cols-5 gap-2.5">
                                                        {Array.from({ length: 10 }).map((_, i) => (
                                                            <div key={i} className="relative group">
                                                                <div 
                                                                    className={`w-full h-10 rounded-xl border-2 cursor-pointer shadow-lg transition-transform group-hover:scale-105 ${config.darkMode ? 'border-white/10' : 'border-neutral-300'}`} 
                                                                    style={{ backgroundColor: (Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6' }} 
                                                                />
                                                                <input 
                                                                    type="color" 
                                                                    value={(Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6'} 
                                                                    onChange={e => handleIndividualColorChange(i, e.target.value)}
                                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </ControlGroup>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rhythm Engine Multipliers (Restored to VISUALIZER TUNING) */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Gauge className="w-3.5 h-3.5 text-cyan-500 opacity-40" />
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-500">Visualizer Tuning</h4>
                    </div>
                    
                    <div className="flex flex-col gap-5">
                        <SmartInput 
                            label="Song Speed (Time Stretch)" 
                            settingKey="rhythmicTimeScale"
                            min={0.1} max={5} step={0.1} 
                            value={config.rhythmicTimeScale} 
                            onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
                            darkMode={config.darkMode} 
                        />

                        <div className="flex flex-col gap-2">
                            <ControlGroup label="Acceleration Mode" settingKey="rhythmicTimeScaleIncreaseMode" darkMode={config.darkMode}>
                                <select 
                                    value={config.rhythmicTimeScaleIncreaseMode || 'NONE'} 
                                    onChange={(e) => setConfig(p => ({...p, rhythmicTimeScaleIncreaseMode: e.target.value as any}))}
                                    className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-900'}`}
                                >
                                    <option value="NONE">NONE</option>
                                    <option value="TIME">TIME BASED</option>
                                    <option value="COLLISION">COLLISION BASED</option>
                                </select>
                            </ControlGroup>
                        </div>

                        <SmartInput 
                            label="Acceleration Rate" 
                            settingKey="rhythmicTimeScaleRate"
                            min={0} max={0.5} step={0.001} 
                            value={config.rhythmicTimeScaleRate || 0.01} 
                            onChange={v => setConfig(p => ({...p, rhythmicTimeScaleRate: v}))} 
                            darkMode={config.darkMode} 
                        />

                        <SmartInput 
                            label="Acceleration Factor" 
                            settingKey="rhythmicTimeScaleAcceleration"
                            min={0} max={0.1} step={0.001} 
                            value={config.rhythmicTimeScaleAcceleration || 0} 
                            onChange={v => setConfig(p => ({...p, rhythmicTimeScaleAcceleration: v}))} 
                            darkMode={config.darkMode} 
                        />

                        <SmartInput 
                            label="Circle Size Multiplier" 
                            settingKey="rhythmicCircleScale"
                            min={0.5} max={8.0} step={0.1} 
                            value={config.rhythmicCircleScale || 1.0} 
                            onChange={v => setConfig(p => ({...p, rhythmicCircleScale: v}))} 
                            darkMode={config.darkMode} 
                        />

                        <p className="text-[7px] font-bold opacity-30 uppercase tracking-[0.1em] text-center leading-relaxed">
                            Higher values expand the orbit range.<br/>
                            The sphere compensates velocity for rhythm sync.
                        </p>
                    </div>
                </div>

                {/* 5. Music Control */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Gauge className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Music Control</h4>
                    </div>
                    <div className="flex flex-col gap-4">
                        <SmartInput label="Music Volume" settingKey="rhythmicVolume" min={0} max={2} step={0.01} value={config.rhythmicVolume} onChange={v => setConfig(p => ({...p, rhythmicVolume: v}))} darkMode={config.darkMode} />
                        <div className="flex items-center gap-4">
                            <ModifierCard 
                                label="Manual Speed" 
                                settingKey="rhythmicDecoupleSpeed"
                                active={config.rhythmicDecoupleSpeed} 
                                onToggle={() => setConfig(p => ({...p, rhythmicDecoupleSpeed: !p.rhythmicDecoupleSpeed}))} 
                                darkMode={config.darkMode}
                                icon={RefreshCcw}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </CollapsibleSection>
  );
};

const ModifierCard = ({ label, active, onToggle, darkMode, icon: Icon, settingKey }: any) => (
    <button 
        onClick={onToggle}
        className={`flex items-center gap-3 p-3 rounded-2xl border transition-all duration-300 group flex-1 ${active ? (darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-white border-zinc-950/10 text-zinc-900 shadow-sm') : 'bg-transparent border-transparent opacity-40 hover:opacity-60 text-zinc-400'}`}
    >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${active ? 'bg-neon-cyan text-white shadow-lg shadow-cyan-500/20' : 'bg-zinc-100 text-zinc-500 group-hover:bg-zinc-300'}`}>
            <Icon className="w-4 h-4" />
        </div>
        <div className="flex flex-col items-start pr-1">
            <div className="flex items-center gap-2">
                <span className="text-[9px] font-black uppercase tracking-tight leading-none whitespace-nowrap">{label}</span>
                <InfoPopup settingKey={settingKey} darkMode={darkMode} />
            </div>
            <span className={`text-[7px] font-bold uppercase mt-1 ${active ? 'text-cyan-500' : 'text-zinc-400'}`}>{active ? 'Synched' : 'Isolated'}</span>
        </div>
    </button>
);
